import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleFilled, CloseOutlined, LoadingOutlined, WarningFilled } from '@ant-design/icons';
import './GoogleSyncToast.css';

export interface GoogleSyncToastState {
  visible: boolean;
  status: 'syncing' | 'success' | 'error';
  syncedCount?: number;
  totalCount?: number;
  errorMessage?: string;
}

interface GoogleSyncToastProps {
  state: GoogleSyncToastState;
  onClose: () => void;
}

export const GoogleSyncToast: React.FC<GoogleSyncToastProps> = ({ state, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (state.visible && state.status === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [state.visible, state.status, onClose]);

  if (!state.visible) return null;

  return (
    <div className={`google-sync-toast google-sync-toast-${state.status}`}>
      <div className="google-sync-toast-content">
        <div className="google-sync-toast-icon-wrap">
          {state.status === 'syncing' && (
            <div className="google-sync-spinner-box">
              <svg className="google-g-icon" viewBox="0 0 24 24" width="22" height="22">
                <path
                  fill="#4285F4"
                  d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                />
                <path
                  fill="#34A853"
                  d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.35 24 12 24z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                />
                <path
                  fill="#EA4335"
                  d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.35 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                />
              </svg>
              <LoadingOutlined className="google-sync-spinner" />
            </div>
          )}

          {state.status === 'success' && (
            <div className="google-sync-success-box">
              <CheckCircleFilled className="google-sync-success-icon" />
            </div>
          )}

          {state.status === 'error' && (
            <div className="google-sync-error-box">
              <WarningFilled className="google-sync-error-icon" />
            </div>
          )}
        </div>

        <div className="google-sync-toast-body">
          <div className="google-sync-toast-title">
            {state.status === 'syncing' && t('googleSync.syncingTitle', 'Синхронизация с Google...')}
            {state.status === 'success' && t('googleSync.successTitle', 'Синхронизация завершена!')}
            {state.status === 'error' && t('googleSync.errorTitle', 'Ошибка синхронизации')}
          </div>

          <div className="google-sync-toast-desc">
            {state.status === 'syncing' && (
              <span>
                {state.totalCount
                  ? t('googleSync.syncingWithCount', {
                      defaultValue: `Обработка контактов (${state.totalCount} шт.)...`,
                      count: state.totalCount,
                    })
                  : t('googleSync.syncingProgress', 'Синхронизируем контакты компании в фоне...')}
              </span>
            )}
            {state.status === 'success' && (
              <span>
                {t('googleSync.successWithCount', {
                  defaultValue: `Успешно синхронизировано контактов: ${state.syncedCount ?? 0}`,
                  count: state.syncedCount ?? 0,
                })}
              </span>
            )}
            {state.status === 'error' && (
              <span>{state.errorMessage || t('googleErrors.syncFailed', 'Не удалось синхронизировать контакты.')}</span>
            )}
          </div>
        </div>

        <button
          type="button"
          className="google-sync-toast-close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseOutlined />
        </button>
      </div>

      {state.status === 'syncing' && (
        <div className="google-sync-progress-bar">
          <div className="google-sync-progress-fill" />
        </div>
      )}
    </div>
  );
};
